import React, { FC } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import Container from 'react-bootstrap/Container';
import { IGameEnvironment } from '../../../model/gameEnvironment/GameEnvironment';
import { IDistribution } from '../../../model/distributions/Distribution';
import { useGameEnvironmentCssClass } from '../common/useGameEnvironmentCssClass';
import { GameEnvironmentSelector } from '../common/GameEnvironmentSelector';
import { useDistributionsController } from '../../../controllers/useDistributionsController';
import { DistributionsNav } from './DistributionsNav';
import { DistributionsTable } from './DistributionsTable';
import { useNavigate } from 'react-router-dom';
import {
    DistributionsPerPageKey,
    useDistributionsSearch
} from './useDistributionsSearch';
import { YakofficePageSize } from '../../components/yakoffice-pagesize/YakofficePageSize';
import { YakofficePagination } from '../../components/yakoffice-pagination/YakofficePagination';
import { runInAction } from 'mobx';
import { useGetDistributionsLink } from '../../routes';
import { GameVersionSelector } from '../common/GameVersionSelector';
import { IGameVersion } from '../../../model/gameVersion/GameVersion';
import { PublisherViewHeader } from '../../components/publisher-view-header/publisher-view-header';
import { usePageTitle } from '../pageTitleContext/PageTitleContext';
import filterStyle from '../../components/tables/filters/table-filters.module.sass';
import style from '../../components/tables/filters/table-filters.module.sass';
import filterIcon from '../../components/tables/filters/filter_list.svg';

export const Distributions: FC = observer(() => {

    // Context
    const cssClass = useGameEnvironmentCssClass();
    const navigate = useNavigate();
    const {
        query,
        distributionsResult,
        distributionsCountResult,
        setPageSize,
        setPageNumber,
        setHideFailed,
        setHideSucceeded
    } = useDistributionsSearch({
        defaultPageSize: parseInt(window.localStorage.getItem(DistributionsPerPageKey) ?? '25'),
        hideFailed: true
    });
    const controller = useDistributionsController();
    const getDistributionsLink = useGetDistributionsLink();
    const { setPageTitle } = usePageTitle();

    // State
    const checkedDistributions = useLocalObservable<IDistribution[]>(() => []);

    setPageTitle(['Distributions']);

    const handleChangePageNumber = (pageNumber: number) => {
        runInAction(() => checkedDistributions.length = 0);
        setPageNumber(pageNumber);
    };

    const handleDistribute = () => {
        // To cause a refresh of distributions
        controller.handleDistribute(() => {
            setHideFailed(query.hideFailed || true);
        });
    };

    const handleDownloadAsJson = () => {
        // To cause a refresh of distributions
        controller.handleDownloadAsJson(() => {
            setHideFailed(query.hideFailed || true);
        });
    };

    const handleCopyDistribution = (toGameEnvironment: IGameEnvironment) => {
        controller.handleCopyDistribution(checkedDistributions.length === 1 ? checkedDistributions[0] : null, toGameEnvironment);
    };

    const handleDistributionCheckedToggle = (distribution: IDistribution) => {
        if (checkedDistributions.includes(distribution))
            runInAction(() => checkedDistributions.splice(checkedDistributions.indexOf(distribution), 1));
        else
            runInAction(() => checkedDistributions.push(distribution));
    };

    const onGameVersionChanged = (targetGv: IGameVersion) => navigate(getDistributionsLink({ gameVersionId: targetGv.id }));
    const onGameEnvironmentChanged = (targetGe: IGameEnvironment) => navigate(getDistributionsLink({ gameEnvironmentId: targetGe.id }));

    return (
        <div id="distributions" className={`${cssClass.name} flexWrapper`}>
            <PublisherViewHeader title="Distributions" customAction={<>
                <GameEnvironmentSelector disabled={false} handleGameEnvironmentChange={onGameEnvironmentChanged} />
                <GameVersionSelector handleGameVersionChange={onGameVersionChanged} />
            </>
            } />

            <Container fluid className="position-relative h-100">
                <div className="position-relative h-100">
                    <DistributionsNav
                        hasDistributions={distributionsResult.distributions != null && distributionsResult.distributions.length > 0}
                        checkedDistributions={checkedDistributions}
                        distributions={distributionsResult.distributions}
                        handleDistribute={handleDistribute}
                        handleCopyDistribution={handleCopyDistribution}
                        handleDownloadAsJson={handleDownloadAsJson}
                    />

                    <div className="mdcDataTableWrapper">
                        <div className={filterStyle.tableFilters}>
                            <img src={filterIcon} alt="" className={style.filterIcon} />
                            <div
                                className={`${filterStyle.status} ${!query.hideSucceeded ? filterStyle.active : filterStyle.inactive}`}
                                onClick={() => setHideSucceeded(!query.hideSucceeded)}>{!query.hideSucceeded &&
                                <i className="fas fa-check" />} Succeeded
                            </div>
                            <div
                                className={`${filterStyle.status} ${!query.hideFailed ? filterStyle.active : filterStyle.inactive}`}
                                onClick={() => setHideFailed(!query.hideFailed)}>{!query.hideFailed &&
                                <i className="fas fa-check" />} Failed
                            </div>
                        </div>
                        <DistributionsTable
                            distributionsResult={distributionsResult}
                            checkedDistributions={checkedDistributions}
                            handleDistributionCheckedToggle={handleDistributionCheckedToggle}
                        />
                        <div className="paginationWrapper">
                            {query.pageSize &&
                                <span className="">Rows per page <YakofficePageSize pageSize={query.pageSize}
                                                                                    handleSetPageSize={setPageSize} />
                        </span>
                            }
                            {(query.pageSize && query.pageNumber && !!distributionsCountResult.count) && (query.pageSize < distributionsCountResult.count) &&
                                <YakofficePagination pageSize={query.pageSize}
                                                     activePageNumber={query.pageNumber}
                                                     itemsCount={distributionsCountResult.count}
                                                     handleSetPageNumber={handleChangePageNumber} />
                            }
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
});
